<script setup>
import { useTemplateRef, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useToggle } from '@vueuse/core';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactNote from 'dashboard/components-next/polaris/cards/ContactNote.vue';

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  writtenBy: {
    type: String,
    required: true,
  },
  allowDelete: {
    type: Boolean,
    default: false,
  },
  collapsible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['delete']);
const noteContentRef = useTemplateRef('noteContentRef');
const needsCollapse = ref(false);
const [isExpanded, toggleExpanded] = useToggle();
const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const handleDelete = () => {
  emit('delete', props.note.id);
};

onMounted(() => {
  if (props.collapsible) {
    // Check if content height exceeds approximately 4 lines
    // Assuming line height is ~1.625 and font size is ~14px
    const threshold = 14 * 1.625 * 4; // ~84px
    needsCollapse.value = noteContentRef.value?.clientHeight > threshold;
  }
});
</script>

<template>
  <ContactNote
    :author="writtenBy"
    :time="`${t('CONTACTS_LAYOUT.SIDEBAR.NOTES.WROTE')} ${dynamicTime(
      note.createdAt
    )}`"
    :deletable="allowDelete"
    @delete="handleDelete"
  >
    <template #avatar>
      <Avatar
        :name="note?.user?.name || 'Bot'"
        :src="
          note?.user?.name
            ? note?.user?.thumbnail
            : '/assets/images/chatwoot_bot.png'
        "
        :size="24"
        rounded-full
      />
    </template>
    <template #content>
      <p
        ref="noteContentRef"
        v-dompurify-html="formatMessage(note.content || '')"
        class="type-polaris-body mb-0 text-polaris-foreground prose-sm prose-p:mb-1 prose-p:mt-0 prose-p:leading-relaxed prose-ul:mb-1 prose-ul:mt-0"
        :class="{
          'line-clamp-4': collapsible && !isExpanded && needsCollapse,
        }"
      />
      <Button
        v-if="collapsible && needsCollapse"
        variant="link"
        color="blue"
        size="sm"
        class="mt-1 px-0 hover:no-underline"
        :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click="() => toggleExpanded()"
      >
        <template v-if="isExpanded">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.COLLAPSE') }}
        </template>
        <template v-else>
          {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EXPAND') }}
        </template>
      </Button>
    </template>
  </ContactNote>
</template>
